import { useMemo, useState } from 'react';
import { CircularProgress, Stack, Typography } from '@mui/material';
import { debounce } from 'lodash';
import AttachmentsItem from '../ProcessedTimesheetOverview/AttachmentsItem';
import UploadFile from '../../../Shared/Components/UploadFile';
import { convertFileSizeToMbString } from '../../../utils/helpers';
import useGetAttachments from '../../../Shared/Services/hooks/Timesheets/useGetAttachments';
import usePostAttachment from '../../../Shared/Services/hooks/Timesheets/usePostAttachment';
import useDownloadAttachment from '../../../Shared/Services/hooks/Timesheets/useDownloadAttachment';
import useDeleteAttachment from '../../../Shared/Services/hooks/Timesheets/useDeleteAttachment';
import usePatchAttachmentLocation from '../../../Shared/Services/hooks/Timesheets/usePatchAttachmentLocation';
import useToast from '../../../utils/useToast';
import { ATTACHMENTS_CONFIG } from '../../../Admin/Configurations/processedTimesheetsConfig';
import { COLORS } from '../../../utils/Application_Constants';

function TimesheetAttachments({ rawTimesheetId, isReadOnly, forLocationId, locations = [] }) {
    const { renderToast, showSuccessToast, showErrorToast } = useToast();
    const [uploadQueue, setUploadQueue] = useState([]);

    const { data: attachments, isLoading, refetch } = useGetAttachments({ rawTimesheetId });

    const { mutate: upload } = usePostAttachment({
        onSuccess: () => {
            refetch();
        },
        onError: (error, variables) => {
            let errorMessage = '';
            const { file } = variables;
            const { status } = error;
            switch (status) {
                case 403:
                    errorMessage = `You are not authorized to upload attachments`;
                    break;
                case 405:
                    errorMessage = `Failed to upload attachment. Maximum number of attachments reached (${ATTACHMENTS_CONFIG.MAX_ITEMS_COUNT}).`;
                    break;
                case 413:
                    errorMessage = `The file ${file.name} is too large. Maximum size is ${ATTACHMENTS_CONFIG.MAX_FILE_SIZE_MB}MB!`;
                    break;
                default:
                    errorMessage = `Failed to upload attachment with name: ${file.name}. Please try again.`;
            }
            showErrorToast(errorMessage);
        },
        onSettled: (data, error, variables) => {
            const { file } = variables;
            setUploadQueue((prev) => prev.filter((item) => item.file.name !== file.name));
        },
    });

    const { mutate: download } = useDownloadAttachment({
        onError: () => showErrorToast('Failed to download attachment.'),
    });

    const { mutate: patchLocation } = usePatchAttachmentLocation({
        onSuccess: () => showSuccessToast(`Attachment location updated successfully`),
        onError: () => showErrorToast(`Failed to update attachment location. Please try again.`),
    });

    const { mutate: deleteAttachment } = useDeleteAttachment({
        onSuccess: () => {
            refetch();
        },
        onError: () => showErrorToast(`Failed to delete attachment. Please try again.`),
    });

    const handleUploadFiles = (files) => {
        // Add files to upload queue
        const newUploads = files.map((file) => ({
            file,
            progress: 0,
            controller: new AbortController(),
        }));

        setUploadQueue((prev) => [...prev, ...newUploads]);

        // Start uploads
        newUploads.forEach((uploadItem) => {
            const { file, controller } = uploadItem;

            // Create progress callback debounced for this specific file
            const onUploadProgress = debounce((progressEvent) => {
                const percentCompleted = Math.round(
                    (progressEvent.loaded * 100) / progressEvent.total
                );
                setUploadQueue((prev) =>
                    prev.map((item) =>
                        item.file.name === file.name
                            ? { ...item, progress: percentCompleted }
                            : item
                    )
                );
            }, 10);

            // Upload the file with progress tracking and abort signal
            upload({ rawTimesheetId, file, onUploadProgress, signal: controller.signal });
        });
    };

    const handleCancelUpload = (fileName) => {
        // Find and abort the upload
        const uploadItem = uploadQueue.find((item) => item.file.name === fileName);
        if (uploadItem?.controller) uploadItem.controller.abort();
        // Remove from upload queue
        setUploadQueue((prev) => prev.filter((item) => item.file.name !== fileName));
    };

    const filteredAttachments = useMemo(() => {
        if (!forLocationId) return attachments;
        return attachments?.filter((item) => item.locationId === forLocationId || !item.locationId);
    }, [attachments, forLocationId]);

    const hasFiles = filteredAttachments?.length > 0 || uploadQueue.length > 0;

    if (isLoading) return <CircularProgress />;

    return (
        <Stack direction="column" gap={2}>
            {renderToast()}
            {!isReadOnly && (
                <>
                    <Typography color={COLORS.LT_JOURNEY175}>Upload timesheet documents</Typography>
                    <UploadFile
                        maxSizeInMB={ATTACHMENTS_CONFIG.MAX_FILE_SIZE_MB}
                        fileTypes={ATTACHMENTS_CONFIG.FILE_TYPES}
                        fileTypesLabels={ATTACHMENTS_CONFIG.FILE_TYPES_LABELS}
                        onAcceptedFiles={handleUploadFiles}
                    />
                </>
            )}
            {hasFiles ? (
                <Stack direction="column" gap={1}>
                    <Typography
                        fontSize={isReadOnly ? '12px' : '14px'}
                        fontWeight={isReadOnly ? '700' : '400'}
                        textTransform={isReadOnly ? 'uppercase' : undefined}
                    >
                        Uploads
                    </Typography>
                    {uploadQueue.map((uploadItem) => (
                        <AttachmentsItem
                            key={uploadItem.file.name}
                            id="upload"
                            name={uploadItem.file.name}
                            type={uploadItem.file.type.split('/')[1]}
                            size={convertFileSizeToMbString(uploadItem.file.size)}
                            isUploading
                            uploadProgress={uploadItem.progress}
                            onCancelUpload={() => handleCancelUpload(uploadItem.file.name)}
                        />
                    ))}
                    {filteredAttachments.map((item) => (
                        <AttachmentsItem
                            key={item.id}
                            id={item.id}
                            name={item.name}
                            type={item.type}
                            size={item.size}
                            url={item.url}
                            source={item.source}
                            isDelible={!isReadOnly}
                            onDownload={(options) => download({ attachmentId: item.id }, options)}
                            onChangeLocation={({ locationId }, options) =>
                                patchLocation({ attachmentId: item.id, locationId }, options)
                            }
                            onDelete={(options) =>
                                deleteAttachment({ attachmentId: item.id }, options)
                            }
                            isReadOnly={isReadOnly}
                            isInlineEdition
                            locationId={item.locationId}
                            locations={locations}
                        />
                    ))}
                </Stack>
            ) : (
                <Typography fontSize="14px" fontWeight="400">
                    No documents attached
                </Typography>
            )}
        </Stack>
    );
}

export default TimesheetAttachments;
